import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, SearchIcon } from '@heroicons/react/outline';
import { feature } from '../utils/features';
import { client } from '../client';
import { homePageJobsQuery } from '../utils/Data';
import Card from './Card';
import { LoadingBlock } from './Spinner';

const MODERN = feature('modernCards');

// Shown while job results load (first load and page changes).
export function ResultsSkeleton() {
  return <LoadingBlock label="Finding jobs…" minHeight="min-h-[60vh]" />;
}

// Refreshed results page frame (feature "modernCards"): back link, heading, two-column cards.
export function ResultsLayout({ heading, children, footer }) {
  const history = useNavigate();
  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <button type="button" onClick={() => history(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-primary-dark">
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        Back
      </button>
      <h1 className="mt-4 font-display text-3xl font-bold text-gray-900">{heading}</h1>
      <div className="mt-8 grid gap-x-4 md:grid-cols-2">{children}</div>
      {footer}
    </div>
  );
}

// Other open jobs, so an empty search still leads somewhere.
function OtherJobs() {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    client.fetch(homePageJobsQuery).then((list) => setJobs((list || []).slice(0, 6))).catch(() => setJobs([]));
  }, []);
  if (jobs === null) return <LoadingBlock label="Loading other jobs…" minHeight="min-h-[12rem]" />;
  if (!jobs.length) return null;
  return (
    <section aria-labelledby="other-jobs" className="mt-14 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="other-jobs" className="font-display text-2xl font-bold text-gray-900">Other jobs hiring now</h2>
        <Link to="/jobs" className="inline-flex items-center gap-1 font-semibold text-primary-dark hover:underline">
          See all jobs <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
      <div className="mt-6 grid gap-x-4 md:grid-cols-2">
        {jobs.map((job) => (
          <Card
            key={job._id}
            btnId={job._id}
            title={job.title}
            companyName={job.companyName}
            categories={job.categories}
            city={job.city}
            salary={job.salary}
            postedOn={job.postedOn}
            externalUrl={job.url}
            source={job.source}
            postExpiry={job.postExpiry}
          />
        ))}
      </div>
    </section>
  );
}

function ModernNoResults({ message, error }) {
  const history = useNavigate();
  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <div className="mx-auto max-w-xl rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200 sm:p-10">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary-dark">
          <SearchIcon className="h-7 w-7" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold text-gray-900">{error ? "We couldn't load jobs right now" : message}</h1>
        <p className="mt-2 text-gray-600">
          {error ? 'Please check your connection and try again.' : 'New jobs are posted often. Try a different search, or see other jobs that are hiring now.'}
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/jobs" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-white shadow-sm transition hover:opacity-90">
            See other jobs <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => history(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 font-semibold text-gray-700 transition hover:border-primary hover:text-primary-dark"
          >
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            Back
          </button>
        </div>
      </div>
      {!error && <OtherJobs />}
    </div>
  );
}

export function NoResults({ message, error = false }) {
  const history = useNavigate();
  if (MODERN) return <ModernNoResults message={message} error={error} />;

  return (
    <div className="flex flex-col items-center justify-center text-center px-4 my-40">
      <SearchIcon className="h-10 w-10 text-primary-dark mb-4" aria-hidden="true" />
      <p className="text-gray-900 text-lg font-semibold mb-1">
        {error ? "We couldn't load jobs right now" : message}
      </p>
      <p className="text-gray-600 mb-6">
        {error
          ? 'Please check your connection and try again.'
          : 'Try a different search, or browse all current postings.'}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link
          to="/jobs"
          className="px-4 py-1 bg-primary rounded text-lg text-white font-semibold"
        >
          See all jobs
        </Link>
        <button
          type="button"
          onClick={() => history(-1)}
          className="px-4 py-1 border-2 border-primary rounded text-lg text-primary-dark font-semibold flex items-center"
        >
          <ChevronLeftIcon className="mr-2 h-5 w-5" aria-hidden="true" />
          Back
        </button>
      </div>
    </div>
  );
}
